import {computed, effect, Signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {
    FormControl,
    FormControlStatus,
    FormGroup,
    NonNullableFormBuilder,
    Validators,
} from '@angular/forms';
import {map} from 'rxjs';
import {AppValidators} from '@brunata-angular/util';
import {isEqual} from 'lodash-es';
import {z} from 'zod';

export const exportEncodings = [
    'UTF_8_BOM',
    'UTF_8',
    'ISO_8859_1',
    'WINDOWS_1252',
    'UTF_16LE',
] as const;

export const exportEncodingSchema = z.enum(exportEncodings);

export type ExportEncoding = z.infer<typeof exportEncodingSchema>;

export type EncodingChoice = {
    id: ExportEncoding;
    token: string;
    qualifierKey: string | null;
};

export const exportEncodingChoices: ReadonlyArray<EncodingChoice> = [
    {id: 'UTF_8_BOM', token: 'UTF-8', qualifierKey: null},
    {
        id: 'UTF_8',
        token: 'UTF-8',
        qualifierKey: 'customizedexports_export_fileformat_encoding_without_bom',
    },
    {
        id: 'ISO_8859_1',
        token: 'ISO-8859-1',
        qualifierKey: 'customizedexports_export_fileformat_encoding_latin_1',
    },
    {id: 'WINDOWS_1252', token: 'Windows-1252', qualifierKey: null},
    {id: 'UTF_16LE', token: 'UTF-16LE', qualifierKey: null},
];

export const fieldSeparators = ['COMMA', 'SEMICOLON', 'TAB', 'PIPE'] as const;

export const fieldSeparatorSchema = z.enum(fieldSeparators);

export type FieldSeparator = z.infer<typeof fieldSeparatorSchema>;

export const fieldSeparatorCharacters: Record<FieldSeparator, string> = {
    COMMA: ',',
    SEMICOLON: ';',
    TAB: '\t',
    PIPE: '|',
};

export const textDelimiters = ['DOUBLE_QUOTE', 'SINGLE_QUOTE', 'NONE'] as const;

export const textDelimiterSchema = z.enum(textDelimiters);

export type TextDelimiter = z.infer<typeof textDelimiterSchema>;

export const textDelimiterCharacters: Record<TextDelimiter, string | null> = {
    DOUBLE_QUOTE: '"',
    SINGLE_QUOTE: "'",
    NONE: null,
};

export const decimalSeparators = ['PERIOD', 'COMMA'] as const;

export const decimalSeparatorSchema = z.enum(decimalSeparators);

export type DecimalSeparator = z.infer<typeof decimalSeparatorSchema>;

export const decimalSeparatorCharacters: Record<DecimalSeparator, string> = {
    PERIOD: '.',
    COMMA: ',',
};

export type CharacterChoice<T extends string> = {
    id: T;
    nameKey: string;
    characterShownInParentheses: string | null;
};

export const fieldSeparatorChoices: ReadonlyArray<CharacterChoice<FieldSeparator>> = [
    {
        id: 'COMMA',
        nameKey: 'customizedexports_export_fileformat_char_comma',
        characterShownInParentheses: ',',
    },
    {
        id: 'SEMICOLON',
        nameKey: 'customizedexports_export_fileformat_char_semicolon',
        characterShownInParentheses: ';',
    },
    {
        id: 'TAB',
        nameKey: 'customizedexports_export_fileformat_char_tab',
        characterShownInParentheses: null,
    },
    {
        id: 'PIPE',
        nameKey: 'customizedexports_export_fileformat_char_pipe',
        characterShownInParentheses: '|',
    },
];

export const textDelimiterChoices: ReadonlyArray<CharacterChoice<TextDelimiter>> = [
    {
        id: 'DOUBLE_QUOTE',
        nameKey: 'customizedexports_export_fileformat_char_double_quote',
        characterShownInParentheses: '"',
    },
    {
        id: 'SINGLE_QUOTE',
        nameKey: 'customizedexports_export_fileformat_char_single_quote',
        characterShownInParentheses: "'",
    },
    {
        id: 'NONE',
        nameKey: 'customizedexports_export_fileformat_char_none',
        characterShownInParentheses: null,
    },
];

export const decimalSeparatorChoices: ReadonlyArray<CharacterChoice<DecimalSeparator>> = [
    {
        id: 'PERIOD',
        nameKey: 'customizedexports_export_fileformat_char_period',
        characterShownInParentheses: '.',
    },
    {
        id: 'COMMA',
        nameKey: 'customizedexports_export_fileformat_char_comma',
        characterShownInParentheses: ',',
    },
];

export const exportDateFormats = [
    'DD_MM_YYYY',
    'YYYY_MM_DD',
    'DD_MM_YYYY_SLASH',
    'MM_DD_YYYY_SLASH',
    'DD_MM_YYYY_DOT',
] as const;

export const exportDateFormatSchema = z.enum(exportDateFormats);

export type ExportDateFormat = z.infer<typeof exportDateFormatSchema>;

export const exportDateFormatLabels: Record<ExportDateFormat, string> = {
    DD_MM_YYYY: 'DD-MM-YYYY',
    YYYY_MM_DD: 'YYYY-MM-DD',
    DD_MM_YYYY_SLASH: 'DD/MM/YYYY',
    MM_DD_YYYY_SLASH: 'MM/DD/YYYY',
    DD_MM_YYYY_DOT: 'DD.MM.YYYY',
};

export const exportDateFormatPatterns: Record<ExportDateFormat, string> = {
    DD_MM_YYYY: 'dd-MM-yyyy',
    YYYY_MM_DD: 'yyyy-MM-dd',
    DD_MM_YYYY_SLASH: 'dd/MM/yyyy',
    MM_DD_YYYY_SLASH: 'MM/dd/yyyy',
    DD_MM_YYYY_DOT: 'dd.MM.yyyy',
};

export const exportTimestampFormats = ['UTC_LABEL', 'OFFSET', 'UTC', 'NO_ZONE'] as const;

export const exportTimestampFormatSchema = z.enum(exportTimestampFormats);

export type ExportTimestampFormat = z.infer<typeof exportTimestampFormatSchema>;

const TIME_OF_DAY_LABEL = 'HH:mm:ss';

const TIME_OF_DAY_PATTERN = 'HH:mm:ss';

const TIME_TO_THE_MINUTE = 'HH:mm';

const isoDateFormat: ExportDateFormat = 'YYYY_MM_DD';

const brunataStandardTimestampFormat: ExportTimestampFormat = 'UTC_LABEL';

const timeZoneParts: Record<
    ExportTimestampFormat,
    {label: string; pattern: string; inUtc: boolean}
> = {
    UTC_LABEL: {label: ' UTC+2', pattern: " 'UTC'Z", inUtc: false},
    OFFSET: {label: '+02:00', pattern: 'ZZ', inUtc: false},
    UTC: {label: 'Z', pattern: "'Z'", inUtc: true},
    NO_ZONE: {label: '', pattern: '', inUtc: false},
};

export type TimestampShape = {label: string; pattern: string; inUtc: boolean};

export function exportTimestampShape(settings: {
    dateFormat: ExportDateFormat;
    timestampFormat: ExportTimestampFormat;
}): TimestampShape {
    const timeZone = timeZoneParts[settings.timestampFormat];
    const writesUtcLabel = settings.timestampFormat === brunataStandardTimestampFormat;
    const writesIsoDate = settings.dateFormat === isoDateFormat && !writesUtcLabel;
    return {
        label:
            exportDateFormatLabels[settings.dateFormat] +
            (writesIsoDate ? 'T' : ' ') +
            (writesUtcLabel ? TIME_TO_THE_MINUTE : TIME_OF_DAY_LABEL) +
            timeZone.label,
        pattern:
            exportDateFormatPatterns[settings.dateFormat] +
            (writesIsoDate ? "'T'" : ' ') +
            (writesUtcLabel ? TIME_TO_THE_MINUTE : TIME_OF_DAY_PATTERN) +
            timeZone.pattern,
        inUtc: timeZone.inUtc,
    };
}

export function exportTimestampFormatOptions(
    dateFormat: ExportDateFormat,
): ReadonlyMap<string, string> {
    return new Map(
        exportTimestampFormats.map(timestampFormat => [
            timestampFormat,
            exportTimestampShape({dateFormat, timestampFormat}).label,
        ]),
    );
}

export const MAX_DECIMAL_PRECISION = 9;

export const PRECISION_OFFERED_WHEN_NOT_AS_STORED = 2;

export const DECIMAL_PRECISION_AS_STORED = null;

export const fixedDecimalPrecisionSchema = z.number().int().min(0).max(MAX_DECIMAL_PRECISION);

export const exportFileFormatSchema = z.object({
    encoding: exportEncodingSchema.catch('UTF_8_BOM'),
    fieldSeparator: fieldSeparatorSchema.catch('COMMA'),
    textDelimiter: textDelimiterSchema.catch('DOUBLE_QUOTE'),
    decimalSeparator: decimalSeparatorSchema.catch('PERIOD'),
    decimalPrecision: fixedDecimalPrecisionSchema.nullable().catch(DECIMAL_PRECISION_AS_STORED),
    dateFormat: exportDateFormatSchema.catch('YYYY_MM_DD'),
    timestampFormat: exportTimestampFormatSchema.catch('UTC_LABEL'),
});

export type ExportFileFormat = z.infer<typeof exportFileFormatSchema>;

export const defaultExportFileFormat: ExportFileFormat = {
    encoding: 'UTF_8_BOM',
    fieldSeparator: 'COMMA',
    textDelimiter: 'DOUBLE_QUOTE',
    decimalSeparator: 'PERIOD',
    decimalPrecision: DECIMAL_PRECISION_AS_STORED,
    dateFormat: 'YYYY_MM_DD',
    timestampFormat: 'UTC_LABEL',
};

export type ExportFileFormatForm = FormGroup<{
    encoding: FormControl<ExportEncoding>;
    fieldSeparator: FormControl<FieldSeparator>;
    textDelimiter: FormControl<TextDelimiter>;
    decimalSeparator: FormControl<DecimalSeparator>;
    exportDecimalsAsStored: FormControl<boolean>;
    decimalPrecision: FormControl<number | null>;
    dateFormat: FormControl<ExportDateFormat>;
    timestampFormat: FormControl<ExportTimestampFormat>;
}>;

export type ExportFileFormatFormValue = ReturnType<ExportFileFormatForm['getRawValue']>;

export function exportFileFormatFormValue(settings: ExportFileFormat): ExportFileFormatFormValue {
    return {
        ...settings,
        exportDecimalsAsStored: settings.decimalPrecision === null,
        decimalPrecision: settings.decimalPrecision ?? PRECISION_OFFERED_WHEN_NOT_AS_STORED,
    };
}

function separatorsCollide(settings: ExportFileFormat): boolean {
    return (
        settings.textDelimiter === 'NONE' &&
        fieldSeparatorCharacters[settings.fieldSeparator] ===
            decimalSeparatorCharacters[settings.decimalSeparator]
    );
}

export function exportFileFormatForm(
    formBuilder: NonNullableFormBuilder,
    settings: ExportFileFormat,
    writesSeparatedText: () => boolean = () => true,
): ExportFileFormatForm {
    const value = exportFileFormatFormValue(settings);
    const form: ExportFileFormatForm = formBuilder.group({
        encoding: formBuilder.control<ExportEncoding>(value.encoding),
        fieldSeparator: formBuilder.control<FieldSeparator>(value.fieldSeparator),
        textDelimiter: formBuilder.control<TextDelimiter>(value.textDelimiter),
        decimalSeparator: formBuilder.control<DecimalSeparator>(value.decimalSeparator),
        exportDecimalsAsStored: formBuilder.control(value.exportDecimalsAsStored),
        decimalPrecision: formBuilder.control<number | null>(value.decimalPrecision, [
            Validators.required,
            AppValidators.integer,
            Validators.min(0),
            Validators.max(MAX_DECIMAL_PRECISION),
        ]),
        dateFormat: formBuilder.control<ExportDateFormat>(value.dateFormat),
        timestampFormat: formBuilder.control<ExportTimestampFormat>(value.timestampFormat),
    });
    form.addValidators(() =>
        writesSeparatedText() && separatorsCollide(exportFileFormatOf(form.getRawValue()))
            ? {separatorsCollide: true}
            : null,
    );
    form.updateValueAndValidity({emitEvent: false});
    return form;
}

export function exportFileFormatOf(value: ExportFileFormatFormValue): ExportFileFormat {
    const {exportDecimalsAsStored, decimalPrecision, ...rest} = value;
    if (exportDecimalsAsStored) {
        return {...rest, decimalPrecision: DECIMAL_PRECISION_AS_STORED};
    }
    const fixed = fixedDecimalPrecisionSchema.safeParse(decimalPrecision);
    return {
        ...rest,
        decimalPrecision: fixed.success ? fixed.data : PRECISION_OFFERED_WHEN_NOT_AS_STORED,
    };
}

export type ExportFileFormatBinding = {
    status: Signal<FormControlStatus>;
    settings: Signal<ExportFileFormat>;
    exportsDecimalsAsStored: Signal<boolean>;
};

export function bindExportFileFormat(
    form: ExportFileFormatForm,
    offersDecimalPrecision: () => boolean = () => true,
): ExportFileFormatBinding {
    const status = toSignal(form.statusChanges, {initialValue: form.status});
    const value = toSignal(form.valueChanges.pipe(map(() => form.getRawValue())), {
        initialValue: form.getRawValue(),
    });
    const exportsDecimalsAsStored = computed(() => value().exportDecimalsAsStored);

    effect(() => {
        if (exportsDecimalsAsStored() || !offersDecimalPrecision()) {
            form.controls.decimalPrecision.disable();
        } else {
            form.controls.decimalPrecision.enable();
        }
    });

    return {status, settings: computed(() => exportFileFormatOf(value())), exportsDecimalsAsStored};
}

export function characterChoiceOptions<T extends string>(
    choices: ReadonlyArray<CharacterChoice<T>>,
    translate: (key: string) => string,
): ReadonlyMap<string, string> {
    return new Map(
        choices.map(choice => {
            const name = translate(choice.nameKey);
            const character = choice.characterShownInParentheses;
            return [choice.id, character === null ? name : `${name} ( ${character} )`];
        }),
    );
}

export function encodingChoiceOptions(
    choices: ReadonlyArray<EncodingChoice>,
    translate: (key: string) => string,
): ReadonlyMap<string, string> {
    return new Map(
        choices.map(choice => [
            choice.id,
            choice.qualifierKey === null
                ? choice.token
                : `${choice.token} (${translate(choice.qualifierKey)})`,
        ]),
    );
}

export function literalLabelOptions<T extends string>(
    idsInDisplayOrder: ReadonlyArray<T>,
    labels: Record<T, string>,
): ReadonlyMap<string, string> {
    return new Map(idsInDisplayOrder.map(id => [id, labels[id]]));
}

export function resetExportFileFormat(form: ExportFileFormatForm): void {
    form.reset(exportFileFormatFormValue(defaultExportFileFormat));
}

export function isDefaultExportFileFormat(settings: ExportFileFormat): boolean {
    return isEqual(settings, defaultExportFileFormat);
}

export type FileFormatSettingsTab = 'characters' | 'numbers' | 'dates';

export type FileFormatCapabilities = {
    characters: boolean;
    numbers: boolean;
    dates: boolean;
    timestamps: boolean;
};

export function visibleFileFormatTabs(
    capabilities: FileFormatCapabilities,
): ReadonlyArray<FileFormatSettingsTab> {
    const tabs: FileFormatSettingsTab[] = capabilities.characters ? ['characters'] : [];
    if (capabilities.numbers) {
        tabs.push('numbers');
    }
    if (capabilities.dates || capabilities.timestamps) {
        tabs.push('dates');
    }
    return tabs;
}
